import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { Icon } from '../../components/Icon';
import { LineMeter } from '../../components/LineMeter';
import { Switch } from '../../components/SettingRow';
import { TwoColumnLayout, Column, Section, SectionBlock, SectionTitle } from '../../components/TwoColumnLayout';
import { ChoiceSwitch } from '../../components/ChoiceSwitch';
import { getUIString } from '../../utils/helpers';
import { ServiceLimitProfileSelect } from '../../components/ServiceLimitProfileSelect';
import { SimCardInfo } from './SimCardInfo';
import { SubscriptionPrivacyForm } from '../../components/SubscriptionPrivacyForm';
import { BusinessCustomerForm } from './BusinessCustomerForm';
import { RoamingLimitSelect } from '../../components/RoamingLimitSelect';
import businessProducts from '../../utils/products';
import IconCopy from '../../static/images/icon_copy.svg';
import QRCode from "react-qr-code";

interface Props {
	id: string;
	simId: string;
	unlimitedData: any;
	voiceMail: any;
	dataSuspended: any;
	internationalCallsAllowed: any;
	roamingLimit: any;
	dataRoamingAllowed: any;
	serviceLimitProfile: any;
	puk: string;
	settingsDisabled: boolean;
	suspendSubscriptionTemporarily: any;
	privacy: any;
	onChange: (setting: any) => void;
	phoneDirectoryUserInfo: any;
	phoneDirectoryOwnerInfo: any;
	firstName: string;
	lastName: string;
	costCenterName: string;
	costCenterAddress: string;
	simClientInfoChangeHandler: (field: string, value: string) => void;
	accountType: string;
	showErrors: boolean;
	usageData: any;
	euDataRoamingLimit: any;
	settingsDisabledNotification: string;
	settingChecked: (setting: any) => string;
	voicemailMsisdn?: string;
	simQrCode?: string;
}

const LeftLabel = styled.label`
	float: left;
`;

const RightAlign = styled.div`
	padding-top: 1rem;
	overflow: hidden; /* if you don't want #second to wrap below #first */
	float: right;
	color: red;
`;

const SettingsDisabledContainer = styled.div`
	border: 1px solid red;
	padding: 5px;
	background-color: hsl(0, 100%, 98%);
`;

const DisabledText = styled.div<{ settingsDisabled: boolean }>`
	color: ${(props) => (props.settingsDisabled ? 'lightgray' : 'inherit')};
`;

const H4 = styled.h4<{ disabled: boolean }>`
	color: ${(props) => (props.disabled ? 'lightgray' : '#4c4c4c')};
	font-weight: 300;
	font-size: 1.25rem;
`;

const EsimActivationCode = styled.div`
	text-align: center;
	margin-bottom: 30px;
	h5 {
		margin-bottom: 0;
	}
	span {
		word-wrap: break-word;
	}
	
	.copy-to-clipboard {
		margin-top: 5px;
		color: #ff0000;
		text-decoration: underline;
		cursor: pointer;
		img {
			margin-right: 5px;
			width: 16px;
			height: auto;
		}
	}
`;

const EsimFaq = styled.div`
	text-align: center;
	padding-bottom: 10px;
	a {
		text-decoration: underline;
		color: #ff0000;
	}
`;

export const SimSettingsBusiness: React.FC<Props> = (props: Props) => {
	const [phoneDirectoryPrivacy, setPhoneDirectoryPrivacy] = useState<string>('');
	const esimActivationCode:string = props.simQrCode ? props.simQrCode.split("$")[2] : "";
	const [copyButtonText, setCopyButtonText] = useState<string>("Kopioi leikepöydälle");

	useEffect(() => {
		setPhoneDirectoryPrivacy(props.phoneDirectoryUserInfo.privacy);
	}, [props.phoneDirectoryUserInfo]);

	const handlePhoneDirectoryPrivacy = (value: string) => {
		if (phoneDirectoryPrivacy !== value) {
			props.onChange({ privacy: { name: 'privacy', value } });
			setPhoneDirectoryPrivacy(value);
		}
	};

	const copyTextToClipboard = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
			console.log("Kopioitu leikepöydälle", text);
			setCopyButtonText("Kopioitu leikepöydälle");
			setTimeout(function () {
				setCopyButtonText("Kopioi leikepöydälle");
			}, 3000);
		} catch (error) {
			console.error('Kopiointi ei onnistunut:', error);
			setCopyButtonText("Kopiointi ei onnistunut");
			setTimeout(function () {
				setCopyButtonText("Kopioi leikepöydälle");
			}, 3000);
		}
	};

	const { settingsDisabled, accountType } = props;
	return (
		<div>
			{settingsDisabled && (
				<SettingsDisabledContainer>{props.settingsDisabledNotification ? props.settingsDisabledNotification : 'Asetuksien muuttaminen ei ole tällä hetkellä mahdollista'}</SettingsDisabledContainer>
			)}
			<TwoColumnLayout>
				<DisabledText settingsDisabled={settingsDisabled}>
					<Column>
						<BusinessCustomerForm
							firstName={props.firstName}
							lastName={props.lastName}
							costCenterName={props.costCenterName}
							costCenterAddress={props.costCenterAddress}
							simClientInfoChangeHandler={props.simClientInfoChangeHandler}
							disabled={settingsDisabled}
						/>
						{!settingsDisabled && <SectionTitle>{getUIString(accountType, 'SimSettings.services')}</SectionTitle>}
						<Section>
							{(props.voiceMail.disabled === undefined || !props.voiceMail.disabled) && (
								<ChoiceSwitch
									setting={props.voiceMail}
									title={getUIString(accountType, 'SimSettings.voiceMail')}
									variable={'voiceMail'}
									onChange={props.onChange}
									disabled={settingsDisabled || props.voiceMail.updating}
									checked={props.settingChecked(props.voiceMail)}
									updating={props.voiceMail.updating}
									voicemailMsisdn={props.voicemailMsisdn}
								/>
							)}
							{(props.unlimitedData.disabled === undefined || !props.unlimitedData.disabled) && (
								<ChoiceSwitch
									setting={props.unlimitedData}
									title={'Rajaton data (+' + businessProducts.getSimUnlimitedDataPrice() + ' €/kk, alv 0%) '}
									variable={'unlimitedData'}
									onChange={props.onChange}
									disabled={settingsDisabled || props.unlimitedData.updating}
									checked={props.settingChecked(props.unlimitedData)}
									updating={props.unlimitedData.updating}
								/>
							)}
						</Section>
						{!settingsDisabled && <SectionTitle>Palvelut Suomessa</SectionTitle>}
						<Section>
							{(props.dataSuspended.disabled === undefined || !props.dataSuspended.disabled) && (
								<ChoiceSwitch
									setting={props.dataSuspended}
									title={'Data-aresti'}
									variable={'dataSuspended'}
									description={'Voit kytkeä netin tilapäisesti pois käytöstä ja takaisin päälle. Palvelu aktivoituu sekunneissa. Huomaathan, että tarvitset nettiä myös YritysMoin käyttöön.'}
									onChange={props.onChange}
									disabled={settingsDisabled || props.dataSuspended.updating}
									checked={props.settingChecked(props.dataSuspended)}
									updating={props.dataSuspended.updating}
								/>
							)}
						</Section>
						<Section>
							{(props.internationalCallsAllowed.disabled === undefined || !props.internationalCallsAllowed.disabled) && (
								<ChoiceSwitch
									setting={props.internationalCallsAllowed}
									title={'Kansainväliset- ja roaming-puhelut'}
									variable={'internationalCallsAllowed'}
									onChange={props.onChange}
									disabled={settingsDisabled || props.internationalCallsAllowed.updating}
									checked={props.settingChecked(props.internationalCallsAllowed)}
									updating={props.internationalCallsAllowed.updating}
								/>
							)}
						</Section>
						<SectionTitle>Palvelut ulkomailla</SectionTitle>
						<Section>
							{props.usageData && !_.isNil(props.usageData.euData) && !_.isNil(props.euDataRoamingLimit) && (
								<SectionBlock>
									<LineMeter
										value={props.usageData.euData}
										maxvalue={parseFloat(props.euDataRoamingLimit.value)}
										color={'#93cb13'}
										additionalText={`${props.usageData.euData.toFixed(2)}Gt dataa käytetty EU-alueella`}
									/>
								</SectionBlock>
							)}
							{(props.roamingLimit.disabled === undefined || !props.roamingLimit.disabled) && (
								<RoamingLimitSelect
									accountType={props.accountType}
									disabled={settingsDisabled || props.roamingLimit.updating}
									onChange={props.onChange}
									value={props.settingChecked(props.roamingLimit)}
									updating={props.roamingLimit.updating}
								/>
							)}
						</Section>
						{(props.serviceLimitProfile.disabled === undefined || !props.serviceLimitProfile.disabled) && (
							<div>
								<ServiceLimitProfileSelect
									disabled={settingsDisabled || props.serviceLimitProfile.updating}
									onChange={(value) => props.onChange({ serviceLimitProfile: { name: 'serviceLimitProfile', value } })}
									accountType={props.accountType}
									value={props.settingChecked(props.serviceLimitProfile)}
									updating={props.serviceLimitProfile.updating}
								/>
							</div>
						)}
					</Column>
					<Column>
						{props.simQrCode &&
							<div>
								<SectionTitle>eSIM</SectionTitle>
								<Section>
									<SectionBlock>
										<h4 style={{marginTop: 0}}>Asennus</h4>
										<p>
											Skannaa tämä QR-koodi sillä laitteella, johon haluat ottaa eSIM-palvelun käyttöön, tai kopioi
											koodin
											merkkijono. Jotkut laitteet tukevat myös QR-koodin skannaamista tai tunnistamista omalta
											näytöltään.
										</p>
										<div style={{height: "auto", margin: "30px auto", maxWidth: 200, width: "100%"}}>
											<QRCode
												size={256}
												style={{height: "auto", maxWidth: "100%", width: "100%"}}
												value={props.simQrCode}
												viewBox={`0 0 256 256`}
											/>
										</div>
										{esimActivationCode &&
											<EsimActivationCode>
												<h5>eSIM -aktivointikoodi</h5>
												<span>{esimActivationCode}</span>
												<div
													className="copy-to-clipboard"
													onClick={() => copyTextToClipboard(esimActivationCode)}
												>
													<img src={IconCopy} alt="Copy"/>
													{copyButtonText}
												</div>
											</EsimActivationCode>
										}
										<EsimFaq>
											<a href="https://www.moi.fi/yrityksille/asiakaspalvelu/" target={"_blank"}
												 rel={"noreferrer"}>Usein kysytyt kysymykset eSIMmistä.</a>.
										</EsimFaq>
									</SectionBlock>
								</Section>
							</div>
						}
						{(props.privacy.disabled === undefined || !props.privacy.disabled) && (
							<div>
								<SubscriptionPrivacyForm
									disabled={settingsDisabled}
									privacy={props.privacy}
									handlePhoneDirectoryPrivacy={handlePhoneDirectoryPrivacy}
									showErrors={props.showErrors}
									phoneDirectoryUserInfo={props.phoneDirectoryUserInfo}
									simClientInfoChangeHandler={props.simClientInfoChangeHandler}
									phoneDirectoryPrivacy={phoneDirectoryPrivacy}
									includeOwnerInfo={true}
									phoneDirectoryOwnerInfo={props.phoneDirectoryOwnerInfo}
									onChange={props.onChange}
									checked={props.phoneDirectoryUserInfo.publishOwner}
								/>
							</div>
						)}
						<SimCardInfo simId={props.simId} puk={props.puk} disabled={settingsDisabled} />
						{(props.suspendSubscriptionTemporarily.disabled === undefined || !props.suspendSubscriptionTemporarily.disabled) && (
							<div>
								<SectionTitle>Hävisikö simmi?</SectionTitle>
								<Section>

										<SectionBlock>
											<Switch
												title="Sulje SIM-kortti väliaikaisesti"
												disabled={settingsDisabled || props.suspendSubscriptionTemporarily.updating}
												updating={props.suspendSubscriptionTemporarily.updating}
												infoText={'Asetusta päivitetään, tämä voi viedä muutaman minuutin'}
												checked={props.settingChecked(props.suspendSubscriptionTemporarily)}
												onChange={(checked: boolean) => props.onChange({ suspendSubscriptionTemporarily: { name: 'suspendSubscriptionTemporarily', value: checked } })}
											/>
										</SectionBlock>

										<SectionBlock>
											<LeftLabel htmlFor="simId">
												<H4 disabled={settingsDisabled}>Vaihda liittymän sim-kortti</H4>
											</LeftLabel>
											{!settingsDisabled && (
												<RightAlign>
													<Link
														to={{
															pathname: '/account/switchSimCard',
															search: '?subscriptionNumber=' + props.id + '&currentSimId=' + props.simId
														}}
													>
														<Icon name="small--edit" />
													</Link>
												</RightAlign>
											)}
										</SectionBlock>

								</Section>
							</div>
						)}
					</Column>
				</DisabledText>
			</TwoColumnLayout>
		</div>
	);
};
